import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import Decimal from 'decimal.js';
import { Plus, Trash2, Send, Save, Ban, ArrowLeft, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardTitle } from '@/components/Card';
import { Button } from '@/components/Button';
import { Field, Input } from '@/components/Input';
import { Combobox } from '@/components/Combobox';
import { NumericInput } from '@/components/NumericInput';
import { DocstatusPill, StatusPill } from '@/components/StatusPill';
import { Timeline } from '@/components/Timeline';
import { Kbd } from '@/components/Kbd';
import { api } from '@/lib/api';
import { money } from '@/lib/format';
import { toast } from '@/components/Toaster';

interface Project  { id: string; name: string; project_name: string; }
interface Customer { id: string; name: string; display_name: string; }
interface Task     { id: string; name: string; subject: string; project_id?: string; }
interface TimesheetEntry {
  id: string; row_index: number;
  project_id?: string;
  task_id?: string;
  description?: string;
  from_time: string;
  to_time: string;
  hours: string;
  is_billable: boolean;
  billing_rate: string;
  billing_amount: string;
}
interface Timesheet {
  id: string; name: string;
  employee_id?: string;
  start_date: string;
  end_date: string;
  customer_id?: string;
  total_hours: string;
  total_billable_hours: string;
  total_billable_amount: string;
  status: 'Draft' | 'Submitted' | 'Billed' | 'Cancelled';
  remarks?: string;
  docstatus: 0 | 1 | 2;
  entries: TimesheetEntry[];
}

interface DraftEntry {
  rowId: string;
  project_id: string;
  task_id: string;
  description: string;
  from_time: string;  // YYYY-MM-DDTHH:mm (input[type=datetime-local])
  to_time: string;
  is_billable: boolean;
  billing_rate: string;
}

const rid = () => Math.random().toString(36).slice(2);
const todayISO = () => new Date().toISOString().slice(0, 10);

function hoursBetween(from: string, to: string): Decimal {
  if (!from || !to) return new Decimal(0);
  const a = new Date(from);
  const b = new Date(to);
  const ms = b.getTime() - a.getTime();
  if (Number.isNaN(ms) || ms <= 0) return new Decimal(0);
  return new Decimal(ms / 3600000).toDecimalPlaces(2);
}

export function TimesheetForm() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: existing } = useQuery({
    queryKey: ['timesheet', id],
    enabled: !isNew,
    queryFn: () => api<Timesheet>(`/projects/timesheets/${id}`),
  });
  const { data: projectsResp  } = useQuery({ queryKey: ['projects'],  queryFn: () => api<{ items: Project[]  }>('/projects/projects') });
  const { data: customersResp } = useQuery({ queryKey: ['customers'], queryFn: () => api<{ items: Customer[] }>('/accounting/customers') });
  const { data: tasksResp     } = useQuery({ queryKey: ['tasks'],     queryFn: () => api<{ items: Task[]     }>('/projects/tasks') });
  const projects  = projectsResp?.items  ?? [];
  const customers = customersResp?.items ?? [];
  const tasks     = tasksResp?.items     ?? [];

  const [employeeID, setEmployeeID] = useState('');
  const [startDate,  setStartDate]  = useState(todayISO());
  const [endDate,    setEndDate]    = useState(todayISO());
  const [customerID, setCustomerID] = useState('');
  const [remarks,    setRemarks]    = useState('');
  const [entries,    setEntries]    = useState<DraftEntry[]>([
    { rowId: rid(), project_id: '', task_id: '', description: '', from_time: '', to_time: '', is_billable: true, billing_rate: '0' },
  ]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!existing) return;
    setEmployeeID(existing.employee_id ?? '');
    setStartDate(existing.start_date.slice(0, 10));
    setEndDate(existing.end_date.slice(0, 10));
    setCustomerID(existing.customer_id ?? '');
    setRemarks(existing.remarks ?? '');
    setEntries(existing.entries.map((e) => ({
      rowId: e.id,
      project_id: e.project_id ?? '',
      task_id: e.task_id ?? '',
      description: e.description ?? '',
      from_time: e.from_time.slice(0, 16),
      to_time:   e.to_time.slice(0, 16),
      is_billable: e.is_billable,
      billing_rate: e.billing_rate,
    })));
  }, [existing]);

  const projectOptions  = useMemo(() => projects.map((p) => ({ value: p.id, label: p.project_name, description: p.name })), [projects]);
  const customerOptions = useMemo(() => customers.map((c) => ({ value: c.id, label: c.display_name, description: c.name })), [customers]);

  function taskOptionsFor(projectID: string) {
    return tasks
      .filter((t) => !projectID || t.project_id === projectID)
      .map((t) => ({ value: t.id, label: t.subject, description: t.name }));
  }

  const totals = useMemo(() => {
    let hours = new Decimal(0);
    let billHours = new Decimal(0);
    let amount = new Decimal(0);
    for (const e of entries) {
      const h = hoursBetween(e.from_time, e.to_time);
      hours = hours.plus(h);
      if (e.is_billable) {
        billHours = billHours.plus(h);
        amount = amount.plus(h.times(new Decimal(e.billing_rate || '0')));
      }
    }
    return { hours, billHours, amount };
  }, [entries]);

  const editable  = isNew || existing?.docstatus === 0;
  const submitted = existing?.docstatus === 1;
  const cancelled = existing?.docstatus === 2;

  function addEntry() {
    setEntries((s) => [...s, { rowId: rid(), project_id: '', task_id: '', description: '', from_time: '', to_time: '', is_billable: true, billing_rate: '0' }]);
  }
  function patch(rowId: string, p: Partial<DraftEntry>) {
    setEntries((s) => s.map((e) => e.rowId === rowId ? { ...e, ...p } : e));
  }
  function remove(rowId: string) {
    setEntries((s) => s.length > 1 ? s.filter((e) => e.rowId !== rowId) : s);
  }

  function validate(): string[] {
    const out: string[] = [];
    if (!startDate || !endDate) out.push('Start and end date are required.');
    if (new Date(endDate) < new Date(startDate)) out.push('End date must be >= start date.');
    const valid = entries.filter((e) => e.from_time && e.to_time);
    if (valid.length === 0) out.push('Add at least one entry.');
    entries.forEach((e, i) => {
      if (!e.from_time || !e.to_time) return;
      if (new Date(e.to_time) <= new Date(e.from_time)) out.push(`Entry ${i + 1}: to-time must be after from-time.`);
    });
    return out;
  }

  function buildBody() {
    return {
      employee_id: employeeID || undefined,
      start_date:  startDate,
      end_date:    endDate,
      customer_id: customerID || undefined,
      remarks:     remarks || undefined,
      entries: entries
        .filter((e) => e.from_time && e.to_time)
        .map((e) => ({
          project_id:   e.project_id || undefined,
          task_id:      e.task_id    || undefined,
          description:  e.description || undefined,
          from_time:    new Date(e.from_time).toISOString(),
          to_time:      new Date(e.to_time).toISOString(),
          is_billable:  !!e.is_billable,
          billing_rate: e.billing_rate || '0',
        })),
    };
  }

  const createMutation = useMutation({
    mutationFn: () => api<Timesheet>('/projects/timesheets', { method: 'POST', body: buildBody() }),
    onSuccess: (t) => {
      toast.success('Draft saved', t.name);
      qc.invalidateQueries({ queryKey: ['doctype', '/projects/timesheets'] });
      navigate({ to: `/projects/timesheets/${t.id}` as never });
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Save failed.'),
  });
  const updateMutation = useMutation({
    mutationFn: () => api<Timesheet>(`/projects/timesheets/${id}`, { method: 'PUT', body: buildBody() }),
    onSuccess: () => {
      toast.success('Saved');
      qc.invalidateQueries({ queryKey: ['timesheet', id] });
      qc.invalidateQueries({ queryKey: ['doctype', '/projects/timesheets'] });
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Save failed.'),
  });
  const submitMutation = useMutation({
    mutationFn: () => api<Timesheet>(`/projects/timesheets/${id}/submit`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Submitted', 'Ready for billing.');
      qc.invalidateQueries({ queryKey: ['timesheet', id] });
      qc.invalidateQueries({ queryKey: ['doctype', '/projects/timesheets'] });
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Submit failed.'),
  });
  const cancelMutation = useMutation({
    mutationFn: () => api<Timesheet>(`/projects/timesheets/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      toast.warning('Cancelled');
      qc.invalidateQueries({ queryKey: ['timesheet', id] });
      qc.invalidateQueries({ queryKey: ['doctype', '/projects/timesheets'] });
    },
    onError: (e: { message?: string }) => setErr(e.message ?? 'Cancel failed.'),
  });

  function onSaveDraft() {
    setErr(null);
    const issues = validate();
    if (issues.length > 0) { setErr(issues.join(' ')); toast.error('Fix these first', issues[0]); return; }
    createMutation.mutate();
  }
  function onSave() {
    setErr(null);
    const issues = validate();
    if (issues.length > 0) { setErr(issues.join(' ')); toast.error('Fix these first', issues[0]); return; }
    updateMutation.mutate();
  }

  const title = isNew ? 'New timesheet' : (existing?.name ?? '…');

  return (
    <>
      <PageHeader
        crumbs={[
          { label: 'Operations', to: '/projects' },
          { label: 'Timesheets', to: '/projects/timesheets' },
          { label: isNew ? 'New' : title },
        ]}
        title={title}
        status={existing && <DocstatusPill docstatus={existing.docstatus} />}
        actions={
          <>
            <Button variant="ghost" asChild>
              <Link to={'/projects/timesheets' as never}><ArrowLeft className="size-4" /> Back</Link>
            </Button>
            {editable && isNew && (
              <Button onClick={onSaveDraft} loading={createMutation.isPending}>
                <Save className="size-4" /> Save draft <Kbd>⌘S</Kbd>
              </Button>
            )}
            {editable && !isNew && (
              <Button variant="secondary" onClick={onSave} loading={updateMutation.isPending}>
                <Save className="size-4" /> Save
              </Button>
            )}
            {editable && !isNew && (
              <Button onClick={() => submitMutation.mutate()} loading={submitMutation.isPending}>
                <Send className="size-4" /> Submit
              </Button>
            )}
            {submitted && (
              <Button variant="danger" onClick={() => cancelMutation.mutate()} loading={cancelMutation.isPending}>
                <Ban className="size-4" /> Cancel
              </Button>
            )}
          </>
        }
      />

      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="flex-1 px-4 sm:px-6 lg:px-8 pt-6 pb-8 grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-4 max-w-[1400px]"
      >
        <div className="space-y-4 min-w-0">
          {cancelled && (
            <Card className="!p-3 flex items-center gap-3 bg-danger/5 border-danger/30">
              <AlertCircle className="size-4 text-danger" />
              <span className="text-body text-danger">Cancelled.</span>
            </Card>
          )}
          {err && (
            <Card className="!p-3 bg-brand-error/5 border-brand-error/30 flex items-start gap-2">
              <AlertCircle className="size-4 text-brand-error mt-0.5 shrink-0" />
              <div className="text-body-sm text-brand-error">{err}</div>
            </Card>
          )}

          <Card>
            <CardTitle>Period</CardTitle>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <Field label="Start date">
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} disabled={!editable} />
              </Field>
              <Field label="End date">
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} disabled={!editable} />
              </Field>
              <Field label="Employee ID" hint="Free-text for now; picker arrives with HR module.">
                <Input value={employeeID} onChange={(e) => setEmployeeID(e.target.value)} disabled={!editable} placeholder="emp_01..." className="font-mono text-caption" />
              </Field>
              <Field label="Customer (for billing)" hint="Optional — used when generating an invoice.">
                <Combobox
                  options={customerOptions}
                  value={customerID || null}
                  onChange={(v) => setCustomerID(v ?? '')}
                  placeholder="— None —" clearable disabled={!editable}
                />
              </Field>
              <Field label="Remarks" >
                <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={!editable} />
              </Field>
            </div>
          </Card>

          <Card padded={false}>
            <div className="flex items-center justify-between p-5 pb-3">
              <CardTitle>Entries</CardTitle>
              {editable && (
                <Button variant="ghost" size="sm" onClick={addEntry}><Plus className="size-4" /> Add entry</Button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-body-sm">
                <thead className="bg-surface-soft border-b border-hairline text-micro-uppercase text-stone">
                  <tr>
                    <th className="text-left font-medium px-3 py-2 w-10">#</th>
                    <th className="text-left font-medium px-3 py-2 min-w-[180px]">Project</th>
                    <th className="text-left font-medium px-3 py-2 min-w-[180px]">Task</th>
                    <th className="text-left font-medium px-3 py-2 min-w-[180px]">From</th>
                    <th className="text-left font-medium px-3 py-2 min-w-[180px]">To</th>
                    <th className="text-right font-medium px-3 py-2 w-20">Hours</th>
                    <th className="text-center font-medium px-3 py-2 w-20">Bill?</th>
                    <th className="text-right font-medium px-3 py-2 w-32">Rate</th>
                    <th className="text-right font-medium px-3 py-2 w-32">Amount</th>
                    <th className="text-left font-medium px-3 py-2 min-w-[180px]">Description</th>
                    {editable && <th className="w-10" />}
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, idx) => {
                    const h = hoursBetween(e.from_time, e.to_time);
                    const amt = h.times(new Decimal(e.billing_rate || '0'));
                    return (
                      <tr key={e.rowId} className="border-t border-hairline">
                        <td className="px-3 py-2 text-stone">{idx + 1}</td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <Combobox
                                options={projectOptions}
                                value={e.project_id || null}
                                onChange={(v) => patch(e.rowId, { project_id: v ?? '', task_id: '' })}
                                placeholder="— None —" clearable
                              />
                            : <span className="px-3 text-charcoal">{projects.find((p) => p.id === e.project_id)?.project_name || '—'}</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <Combobox
                                options={taskOptionsFor(e.project_id)}
                                value={e.task_id || null}
                                onChange={(v) => patch(e.rowId, { task_id: v ?? '' })}
                                placeholder="— None —" clearable
                              />
                            : <span className="px-3 text-charcoal">{tasks.find((t) => t.id === e.task_id)?.subject || '—'}</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <Input type="datetime-local" value={e.from_time} onChange={(ev) => patch(e.rowId, { from_time: ev.target.value })} />
                            : <span className="px-3 text-charcoal">{e.from_time}</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <Input type="datetime-local" value={e.to_time} onChange={(ev) => patch(e.rowId, { to_time: ev.target.value })} />
                            : <span className="px-3 text-charcoal">{e.to_time}</span>}
                        </td>
                        <td className="px-3 py-2 text-right num">{h.toString()}</td>
                        <td className="px-3 py-2 text-center">
                          {editable
                            ? <input type="checkbox" className="size-4 accent-accent" checked={e.is_billable} onChange={(ev) => patch(e.rowId, { is_billable: ev.target.checked })} />
                            : e.is_billable ? <StatusPill tone="success" withDot={false}>Yes</StatusPill> : <StatusPill tone="neutral" withDot={false}>No</StatusPill>}
                        </td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <NumericInput value={e.billing_rate} onChange={(v) => patch(e.rowId, { billing_rate: v })} decimalPlaces={0} />
                            : <span className="num text-right block px-3">{money(e.billing_rate)}</span>}
                        </td>
                        <td className="px-3 py-2 text-right num">{e.is_billable ? money(amt.toString()) : '—'}</td>
                        <td className="px-2 py-1.5">
                          {editable
                            ? <Input value={e.description} onChange={(ev) => patch(e.rowId, { description: ev.target.value })} />
                            : <span className="px-3 text-charcoal">{e.description || '—'}</span>}
                        </td>
                        {editable && (
                          <td className="px-2 py-1.5">
                            <Button variant="ghost" size="icon" aria-label="Remove" onClick={() => remove(e.rowId)} disabled={entries.length <= 1}>
                              <Trash2 className="size-3.5" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-4 sm:px-5 py-3 border-t border-hairline flex flex-wrap items-center justify-end gap-x-4 gap-y-2 num">
              <div className="text-caption text-stone">Hours</div>
              <div className="font-medium">{totals.hours.toString()}</div>
              <div className="text-caption text-stone">Billable</div>
              <div className="font-medium">{totals.billHours.toString()}</div>
              <div className="text-caption text-stone">Amount</div>
              <div className="font-medium">{money(totals.amount.toString())}</div>
            </div>
          </Card>
        </div>

        <aside className="space-y-4">
          <Card>
            <CardTitle>Status</CardTitle>
            <div className="mt-3 space-y-2 text-body-sm">
              <Row label="Docstatus" pill={existing ? <DocstatusPill docstatus={existing.docstatus} /> : <StatusPill tone="neutral">Draft</StatusPill>} />
              {existing && (
                <>
                  <Row label="Status"   value={existing.status} />
                  <Row label="Hours"    value={existing.total_hours} />
                  <Row label="Billable" value={existing.total_billable_hours} />
                  <Row label="Amount"   value={money(existing.total_billable_amount)} />
                </>
              )}
            </div>
          </Card>

          <Card>
            <CardTitle>How it bills</CardTitle>
            <p className="mt-2 text-caption text-stone">
              Submit does not post to the GL. Billable entries feed the "Make Invoice from Timesheet" flow, which creates a Sales Invoice line per billable entry.
            </p>
          </Card>

          {!isNew && existing && (
            <Timeline doctype="timesheet" documentId={existing.id} />
          )}
        </aside>
      </motion.div>
    </>
  );
}

function Row({ label, value, pill }: { label: string; value?: string; pill?: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-stone">{label}</span>
      {pill ?? <span className="text-ink num">{value}</span>}
    </div>
  );
}
